import React, { type PropsWithChildren } from 'react';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useIsDensityFull,
	useIsVirtualizationEnabled,
	useIsColumnResizingEnabled,
	useIsFixedTableLayoutEnabled,
	useIsInlineEditingEnabled,
	useIsIssueCreateEnabled,
	useIsInfiniteScrollEnabled,
	useIsSiblingIssueCreateEnabled,
	useIsChildIssueCreateEnabled,
	useIsIssueRankDescending,
	useIsIssueHierarchyEnabled,
	useIsIssueOperationMeatballMenuEnabled,
	useIsColumnBordersEnabled,
	useIsRowReorderingEnabled,
	useIsEntireRowDraggable,
	useIsGridNavigationEnabled,
	useIsInlineFieldConfigEnabled,
	useIsInBetweenColumnPickerEnabled,
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
	useIsHideDoneItemsEnabled,
	useIsMergedColumnEnabled,
	useIsMergedColumnVisible,
	useIsChildIssuesTableVisualUpliftEnabled,
	useIsStickyColumnsEnabled,
	useIsStickyColumnsVisible,
	useIsAutoUnstickyOnSmallScreenEnabled,
	useIsConditionalFormattingEnabled,
	useIsTimelineEnabled,
	useIsProjectQuickstartOnboardingEnabled,
	useIsOpenIssueLinkEnabled,
	useIsSkipIssueCreateSuccessFlagEnabled,
	useIsInBetweenStickyColumnPickerEnabled,
	useIsColumnConfigColumnEnabled,
	useIsRemoveColumnEnabled,
} from './selectors.tsx';
import { FeaturesContainerInternal } from './store.tsx';
import type { Props } from './types.tsx';

const ContextualFeatureAnalytics = ({ children }: PropsWithChildren<{}>) => {
	const isDensityFull = useIsDensityFull();
	const isVirtualizationEnabled = useIsVirtualizationEnabled();
	const isColumnResizingEnabled = useIsColumnResizingEnabled();
	const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();
	const isInlineEditingEnabled = useIsInlineEditingEnabled();
	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	const isIssueCreateEnabled = useIsIssueCreateEnabled();
	const isInfiniteScrollEnabled = useIsInfiniteScrollEnabled();
	const isSiblingIssueCreateEnabled = useIsSiblingIssueCreateEnabled();
	const isChildIssueCreateEnabled = useIsChildIssueCreateEnabled();
	const isIssueRankDescending = useIsIssueRankDescending();
	const isIssueHierarchyEnabled = useIsIssueHierarchyEnabled();
	const isIssueOperationMeatballMenuEnabled = useIsIssueOperationMeatballMenuEnabled();
	const isColumnBordersEnabled = useIsColumnBordersEnabled();
	const isRowReorderingEnabled = useIsRowReorderingEnabled();
	const isEntireRowDraggable = useIsEntireRowDraggable();
	const isGridNavigationEnabled = useIsGridNavigationEnabled();
	const isInlineFieldConfigEnabled = useIsInlineFieldConfigEnabled();
	const isInBetweenColumnPickerEnabled = useIsInBetweenColumnPickerEnabled();
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	const isHideDoneItemsEnabled = useIsHideDoneItemsEnabled();
	const isMergedColumnEnabled = useIsMergedColumnEnabled();
	const isMergedColumnVisible = useIsMergedColumnVisible();
	const isChildIssuesTableVisualUpliftEnabled = useIsChildIssuesTableVisualUpliftEnabled();
	const isStickyColumnsEnabled = useIsStickyColumnsEnabled();
	const isStickyColumnsVisible = useIsStickyColumnsVisible();
	const isAutoUnstickyOnSmallScreenEnabled = useIsAutoUnstickyOnSmallScreenEnabled();
	let isConditionalFormattingEnabled = false;
	if (fg('jira_list_conditional_formatting')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isConditionalFormattingEnabled = useIsConditionalFormattingEnabled();
	}
	const isTimelineEnabled = useIsTimelineEnabled();
	const isOpenIssueLinkEnabled = fg('jira_issue_table_open_issue_link_config')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIsOpenIssueLinkEnabled()
		: true;
	const isProjectQuickstartOnboardingEnabled = useIsProjectQuickstartOnboardingEnabled();
	const isSkipIssueCreateSuccessFlagEnabled = useIsSkipIssueCreateSuccessFlagEnabled();

	const isInBetweenStickyColumnPickerEnabled = fg('jira_timeline_sticky_assignee_status_columns')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIsInBetweenStickyColumnPickerEnabled()
		: false;

	const isColumnConfigColumnEnabled = fg('jira_timeline_sticky_assignee_status_columns')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIsColumnConfigColumnEnabled()
		: true;
	const isRemoveColumnEnabled = fg('jira_issue_table_remove_column_check')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIsRemoveColumnEnabled()
		: true;

	return (
		<ContextualAnalyticsData
			attributes={{
				issueTableFeatures: {
					isDensityFull,
					isVirtualizationEnabled,
					isColumnResizingEnabled,
					isFixedTableLayoutEnabled,
					isInlineEditingEnabled,
					isInlineEditingExtendedFieldSupportEnabled,
					isIssueCreateEnabled,
					isInfiniteScrollEnabled,
					isSiblingIssueCreateEnabled,
					isChildIssueCreateEnabled,
					isIssueRankDescending,
					isIssueHierarchyEnabled,
					isIssueOperationMeatballMenuEnabled,
					isColumnBordersEnabled,
					isRowReorderingEnabled,
					isEntireRowDraggable,
					isGridNavigationEnabled,
					isInlineFieldConfigEnabled,
					isInBetweenColumnPickerEnabled,
					isSingleLineRowHeightEnabled,
					isHideDoneItemsEnabled,
					isMergedColumnEnabled,
					isMergedColumnVisible,
					isChildIssuesTableVisualUpliftEnabled,
					isStickyColumnsEnabled,
					isStickyColumnsVisible,
					isAutoUnstickyOnSmallScreenEnabled,
					isConditionalFormattingEnabled,
					isTimelineEnabled,
					isOpenIssueLinkEnabled,
					isProjectQuickstartOnboardingEnabled,
					isSkipIssueCreateSuccessFlagEnabled,
					isInBetweenStickyColumnPickerEnabled,
					isColumnConfigColumnEnabled,
					isRemoveColumnEnabled,
				},
			}}
		>
			{children}
		</ContextualAnalyticsData>
	);
};

export const FeaturesContainer = ({ children, ...props }: PropsWithChildren<Props>) => {
	return (
		<FeaturesContainerInternal {...props}>
			<ContextualFeatureAnalytics>{children}</ContextualFeatureAnalytics>
		</FeaturesContainerInternal>
	);
};
